import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../context/AuthContext.jsx';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getMondayOf(dateStr) {
  const d = new Date(dateStr + 'T00:00:00Z');
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

function getMondayOfToday() {
  const d = new Date();
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'pm' : 'am';
  const h12 = hour % 12 || 12;
  return m === '00' ? `${h12}${ampm}` : `${h12}:${m}${ampm}`;
}

function roleLabel(r) {
  return r?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00:00Z').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

function shiftHours(start, end) {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return ((eh * 60 + em) - (sh * 60 + sm)) / 60;
}

export default function OpenShiftsPage() {
  const api = useApi();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';

  const [openShifts, setOpenShifts] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [weekStart, setWeekStart] = useState(getMondayOfToday);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get('/roster/open-shifts');
      setOpenShifts(data.open_shifts || []);
      setMyRequests(data.my_requests || []);

      // Owner: load all pending requests across all rosters
      if (isOwner) {
        const pending = await api.get('/roster/pending-requests');
        setPendingRequests(pending || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load open shifts');
    } finally {
      setLoading(false);
    }
  }, [isOwner]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  async function handleRequest(shiftId) {
    if (!window.confirm('Request this shift? Your manager will be notified for approval.')) return;
    setRequesting(shiftId);
    try {
      await api.post(`/shifts/${shiftId}/request`);
      await load();
    } catch (err) {
      alert(err.message || 'Failed to request shift');
    } finally {
      setRequesting(null);
    }
  }

  async function handleCancelRequest(requestId) {
    setCancelling(requestId);
    try {
      await api.delete(`/shifts/requests/${requestId}`);
      await load();
    } catch (err) {
      alert(err.message || 'Failed to cancel request');
    } finally {
      setCancelling(null);
    }
  }

  async function handleReview(requestId, approved) {
    setReviewing(requestId);
    try {
      await api.put(`/shifts/requests/${requestId}/approve`, { approved });
      await load();
    } catch (err) {
      alert(err.message || 'Failed to process request');
    } finally {
      setReviewing(null);
    }
  }

  const hasRequestedShift = (shiftId) =>
    myRequests.some(r => r.shift_id === shiftId && r.status === 'pending');

  const getRequestId = (shiftId) =>
    myRequests.find(r => r.shift_id === shiftId && r.status === 'pending')?.id;

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: 64 }}>
          <span className="spinner spinner-dark" style={{ width: 28, height: 28 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Open Shifts</h1>
          <p className="page-subtitle">
            {isOwner
              ? 'Manage open shifts and approve staff requests'
              : 'Pick up available shifts'}
          </p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* ── Owner: Pending Requests ── */}
      {isOwner && pendingRequests.length > 0 && (
        <div className="card mb-4">
          <div className="card-header">
            <span className="card-title">Pending Requests</span>
            <span className="badge badge-warning">{pendingRequests.length}</span>
          </div>
          <div className="table-wrap" style={{ borderRadius: 0, border: 'none' }}>
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Role</th>
                  <th>Time</th>
                  <th>Requested</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map(req => (
                  <tr key={req.id}>
                    <td data-label="Employee"><strong>{req.requester_name}</strong></td>
                    <td data-label="Date">{req.date ? formatDate(req.date) : '—'}</td>
                    <td data-label="Role"><span className="badge badge-primary">{roleLabel(req.role)}</span></td>
                    <td data-label="Time">
                      {req.start_time && req.end_time
                        ? `${formatTime(req.start_time)} – ${formatTime(req.end_time)}`
                        : '—'}
                    </td>
                    <td data-label="Requested" className="text-muted text-sm">
                      {new Date(req.requested_at).toLocaleDateString('en-AU', {
                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={reviewing === req.id}
                          onClick={() => handleReview(req.id, true)}
                        >
                          {reviewing === req.id ? <span className="spinner" /> : 'Approve'}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--color-danger)' }}
                          disabled={reviewing === req.id}
                          onClick={() => handleReview(req.id, false)}
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Open Shifts Calendar ── */}
      <div className="card">
        <div className="card-header open-shifts-card-header">
          <span className="card-title">
            {isOwner ? 'All Open Shifts' : 'Available Shifts'}
          </span>
          <div className="flex items-center gap-2 open-shifts-nav" style={{ flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setWeekStart(w => addDays(w, -7))}>‹</button>
            <span style={{ fontSize: 13, fontWeight: 500, textAlign: 'center' }}>
              {new Date(weekStart + 'T00:00:00Z').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
              {' – '}
              {new Date(addDays(weekStart, 6) + 'T00:00:00Z').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            <button className="btn btn-secondary btn-sm" onClick={() => setWeekStart(w => addDays(w, 7))}>›</button>
          </div>
        </div>

        {(() => {
          // Build days for this week
          const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
          const shiftsThisWeek = openShifts.filter(s => s.date >= weekStart && s.date <= weekDays[6]);
          const totalThisWeek = shiftsThisWeek.length;

          if (openShifts.length === 0) {
            return (
              <div className="empty-state" style={{ padding: 48 }}>
                <div className="empty-state-icon">✅</div>
                <h3>No open shifts</h3>
                <p>{isOwner
                  ? 'All shifts are filled. Mark a shift as open from the Roster page.'
                  : 'There are no open shifts right now. Check back later.'}</p>
              </div>
            );
          }

          return (
            <div style={{ overflowX: 'auto' }}>
              {totalThisWeek === 0 && (
                <div style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                  No open shifts this week.
                  {openShifts.length > 0 && (() => {
                    const next = getMondayOf(openShifts.sort((a,b) => a.date.localeCompare(b.date))[0].date);
                    return next > weekStart
                      ? <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }} onClick={() => setWeekStart(next)}>Jump to next →</button>
                      : null;
                  })()}
                </div>
              )}
              <div className="open-shifts-calendar" style={{ minWidth: 700 }}>
                {weekDays.map((date, i) => {
                  const dayShifts = openShifts.filter(s => s.date === date);
                  const isToday = date === new Date().toISOString().slice(0, 10);
                  return (
                    <div key={date} style={{
                      borderRight: i < 6 ? '1px solid var(--border)' : 'none',
                      minHeight: 120,
                    }}>
                      <div style={{
                        padding: '6px 8px',
                        borderBottom: '1px solid var(--border)',
                        background: isToday ? 'var(--color-primary-light, #eff6ff)' : 'var(--bg-subtle, #f9fafb)',
                        fontWeight: 600,
                        fontSize: 12,
                        color: isToday ? 'var(--color-primary)' : 'var(--text-muted)',
                      }}>
                        {DAY_LABELS[i]}{' '}
                        <span style={{ fontWeight: 400 }}>
                          {new Date(date + 'T00:00:00Z').getUTCDate()}
                        </span>
                      </div>
                      <div style={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {dayShifts.map(shift => {
                          const alreadyRequested = hasRequestedShift(shift.id);
                          const hours = shiftHours(shift.start_time, shift.end_time);
                          return (
                            <div key={shift.id} style={{
                              background: 'var(--color-warning-light, #fffbeb)',
                              border: '1px solid var(--color-warning, #f59e0b)',
                              borderRadius: 6,
                              padding: '5px 7px',
                              fontSize: 11,
                            }}>
                              <div style={{ fontWeight: 600, marginBottom: 2 }}>{roleLabel(shift.role)}</div>
                              <div style={{ color: 'var(--text-muted)' }}>
                                {formatTime(shift.start_time)}–{formatTime(shift.end_time)} ({hours.toFixed(1)}h)
                              </div>
                              {!isOwner && (
                                <div style={{ marginTop: 4 }}>
                                  {alreadyRequested ? (
                                    <button
                                      className="btn btn-sm"
                                      style={{ fontSize: 11, padding: '2px 8px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', color: 'var(--color-danger, #ef4444)', cursor: 'pointer' }}
                                      disabled={cancelling === getRequestId(shift.id)}
                                      onClick={() => handleCancelRequest(getRequestId(shift.id))}
                                    >
                                      {cancelling === getRequestId(shift.id) ? <span className="spinner" /> : 'Cancel'}
                                    </button>
                                  ) : (
                                    <button
                                      className="btn btn-primary btn-sm"
                                      style={{ fontSize: 11, padding: '2px 8px' }}
                                      disabled={requesting === shift.id}
                                      onClick={() => handleRequest(shift.id)}
                                    >
                                      {requesting === shift.id ? <span className="spinner" /> : 'Request'}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Staff: My Pending Requests ── */}
      {!isOwner && myRequests.length > 0 && (
        <div className="card mt-4">
          <div className="card-header">
            <span className="card-title">My Pending Requests</span>
          </div>
          <div className="table-wrap" style={{ borderRadius: 0, border: 'none' }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Role</th>
                  <th>Time</th>
                  <th>Status</th>
                  <th>Requested</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {myRequests.map(req => (
                  <tr key={req.id}>
                    <td data-label="Date">
                      {req.date
                        ? new Date(req.date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
                        : '—'}
                    </td>
                    <td data-label="Role"><span className="badge badge-primary">{roleLabel(req.role)}</span></td>
                    <td data-label="Time">
                      {req.start_time && req.end_time
                        ? `${formatTime(req.start_time)} – ${formatTime(req.end_time)}`
                        : '—'}
                    </td>
                    <td data-label="Status">
                      <span className={`badge badge-${req.status === 'pending' ? 'warning' : req.status === 'approved' ? 'success' : 'secondary'}`}>
                        {req.status}
                      </span>
                    </td>
                    <td data-label="Requested" className="text-muted text-sm">
                      {new Date(req.requested_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                    </td>
                    <td>
                      {req.status === 'pending' && (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--color-danger, #ef4444)', fontSize: 12 }}
                          disabled={cancelling === req.id}
                          onClick={() => handleCancelRequest(req.id)}
                        >
                          {cancelling === req.id ? <span className="spinner" /> : 'Cancel'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
